import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from './ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { API_BASE_URL } from '../config';
import { FUNCTION_NAMES } from '../constants';
import { fetchWithRetry } from '../utils/tourUtils';
import { useNavigate } from 'react-router-dom';
import { 
  Camera, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Play,
  Eye,
  Loader2,
  MapPin,
  Calendar,
  User,
  ImageIcon,
  Zap
} from 'lucide-react';

const SyncTourManager = () => {
  const [pendingSyncTours, setPendingSyncTours] = useState([]);
  const [allSyncTours, setAllSyncTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [generatingTours, setGeneratingTours] = useState(new Set());
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSyncTours();
    const interval = setInterval(fetchSyncTours, 45000); // Refresh every 45 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchSyncTours = async () => {
    setLoading(true);
    try {
      // Fetch pending sync tour items
      const pendingResponse = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'syncTour.findPendingItems',
          payload: { limit: 100 }
        },
        label: 'pending sync tours'
      });

      // Fetch all recent sync tour items
      const allResponse = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'syncTour.findMany',
          payload: { 
            orderBy: { createdAt: 'desc' },
            take: 200
          }
        },
        label: 'all sync tours'
      });

      setPendingSyncTours(pendingResponse || []);
      setAllSyncTours(allResponse || []);
    } catch (error) {
      console.error('Error fetching sync tours:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch sync tour items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateTourForSpace = async (spaceId, spaceName) => {
    setGeneratingTours(prev => new Set([...prev, spaceId]));
    
    try {
      // First, get all synced 360° images for this space
      const spaceImages = allSyncTours.filter(
        item => item.spaceId === spaceId && item.syncStatus === 'SYNCED'
      );

      if (spaceImages.length === 0) {
        toast({
          title: 'Error',
          description: 'No synced 360° images found for this space',
          variant: 'destructive'
        });
        return;
      }

      // Create tour with the synced images
      const tourData = {
        name: `${spaceName} - 360° Tour`,
        spaceId: spaceId,
        projectId: spaceImages[0].projectId || 1, // You may need to fetch this from space data
        lotId: spaceImages[0].lotId || 1,
        buildingId: spaceImages[0].buildingId || 1,
        floorId: spaceImages[0].floorId || 1,
        tourType: 'TRUM_TICHNON'
      };

      // Create the tour
      const newTour = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'tour.create',
          payload: tourData
        },
        label: 'create tour from sync'
      });

      // Add images to the tour
      for (const syncItem of spaceImages) {
        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'tourImage.create',
            payload: {
              tourId: newTour.id,
              imageKey: syncItem.s3Path,
              name: `360° Image - ${syncItem.spaceName}`,
              description: `Generated from sync tour ${syncItem.id}`
            }
          },
          label: 'add tour image'
        });

        // Update sync tour status to indicate tour has been generated
        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'syncTour.update',
            payload: {
              id: syncItem.id,
              data: {
                tourGenerated: true,
                tourId: newTour.id,
                syncStatus: 'READY'
              }
            }
          },
          label: 'update sync tour status'
        });
      }

      toast({
        title: 'Success',
        description: `Tour "${tourData.name}" created successfully with ${spaceImages.length} images`,
      });

      // Refresh data
      fetchSyncTours();

    } catch (error) {
      console.error('Error generating tour:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate tour',
        variant: 'destructive'
      });
    } finally {
      setGeneratingTours(prev => {
        const newSet = new Set(prev);
        newSet.delete(spaceId);
        return newSet;
      });
    }
  };

  const markAsReady = async (syncTourId) => {
    try {
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'syncTour.update',
          payload: {
            id: syncTourId,
            data: {
              syncStatus: 'READY'
            }
          }
        },
        label: 'mark sync tour as ready'
      });

      toast({
        title: 'Success',
        description: 'Sync tour marked as ready'
      });
      fetchSyncTours();
    } catch (error) {
      console.error('Error updating sync tour:', error);
      toast({
        title: 'Error',
        description: 'Failed to update sync tour status',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { variant: 'secondary', icon: Clock, text: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      SYNCED: { variant: 'default', icon: CheckCircle, text: 'Synced', color: 'bg-blue-100 text-blue-800' },
      FAILED: { variant: 'destructive', icon: XCircle, text: 'Failed' },
      PROCESSING: { variant: 'default', icon: Loader2, text: 'Processing', color: 'bg-purple-100 text-purple-800' },
      READY: { variant: 'default', icon: CheckCircle, text: 'Ready', color: 'bg-green-100 text-green-800' }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={config.color}>
        <Icon className={`w-3 h-3 mr-1 ${status === 'PROCESSING' ? 'animate-spin' : ''}`} />
        {config.text}
      </Badge>
    );
  };

  const groupBySpace = (items) => {
    const groups = {};
    items.forEach(item => {
      if (!groups[item.spaceId]) {
        groups[item.spaceId] = {
          spaceId: item.spaceId,
          spaceName: item.spaceName,
          items: []
        };
      }
      groups[item.spaceId].items.push(item);
    });
    return Object.values(groups);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const SyncTourCard = ({ syncTour, showActions = false }) => {
    const isGenerating = generatingTours.has(syncTour.spaceId);

    return (
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <Camera className="w-5 h-5 text-blue-600" />
              <div>
                <h3 className="font-medium">{syncTour.spaceName}</h3>
                <p className="text-sm text-gray-500">Space ID: {syncTour.spaceId}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {syncTour.tourGenerated && (
                <Badge variant="default" className="bg-green-100 text-green-800">
                  <Play className="w-3 h-3 mr-1" />
                  Tour Generated
                </Badge>
              )}
              {getStatusBadge(syncTour.syncStatus)}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <span>User: {syncTour.userId}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span>{formatDate(syncTour.captureDate)}</span>
            </div>
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-gray-400" />
              <span>File: {syncTour.filePath?.split('/').pop() || 'Unknown'}</span>
            </div>
            {syncTour.fileSize && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span>{formatFileSize(syncTour.fileSize)}</span>
              </div>
            )}
          </div>

          {syncTour.s3Path && (
            <div className="text-xs text-gray-500 mb-3">
              <strong>S3 Path:</strong> {syncTour.s3Path}
            </div>
          )}

          {showActions && (
            <div className="flex gap-2 mt-4">
              {syncTour.syncStatus === 'SYNCED' && !syncTour.tourGenerated && (
                <Button
                  size="sm"
                  onClick={() => markAsReady(syncTour.id)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Mark Ready
                </Button>
              )}
              
              {syncTour.tourId && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/tour-editor/${syncTour.tourId}`)}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  View Tour
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const SpaceGroupCard = ({ group, showActions = false }) => {
    const syncedItems = group.items.filter(item => item.syncStatus === 'SYNCED');
    const readyItems = group.items.filter(item => item.syncStatus === 'READY');
    const hasGeneratedTour = group.items.some(item => item.tourGenerated);
    const isGenerating = generatingTours.has(group.spaceId);

    return (
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-blue-600" />
              <div>
                <CardTitle className="text-lg">{group.spaceName}</CardTitle>
                <p className="text-sm text-gray-500">
                  {group.items.length} images • Space ID: {group.spaceId}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasGeneratedTour && (
                <Badge variant="default" className="bg-green-100 text-green-800">
                  <Play className="w-3 h-3 mr-1" />
                  Tour Generated
                </Badge>
              )}
              <Badge variant="secondary">
                {syncedItems.length}/{group.items.length} Synced
              </Badge>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-3">
            {group.items.map(item => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Camera className="w-4 h-4 text-gray-400" />
                  <div className="text-sm">
                    <div className="font-medium">
                      {item.filePath?.split('/').pop() || `Image ${item.id.slice(-6)}`}
                    </div>
                    <div className="text-gray-500">
                      {formatDate(item.captureDate)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(item.syncStatus)}
                </div>
              </div>
            ))}
          </div>

          {showActions && syncedItems.length > 0 && !hasGeneratedTour && (
            <div className="mt-4 pt-4 border-t">
              <Button
                onClick={() => generateTourForSpace(group.spaceId, group.spaceName)}
                disabled={isGenerating}
                className="w-full"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Tour...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Generate Tour ({syncedItems.length} images)
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const pendingGroups = groupBySpace(pendingSyncTours);
  const allGroups = groupBySpace(allSyncTours);

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">360° Tour Sync Management</h1>
        <Button onClick={fetchSyncTours} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
            <div className="text-2xl font-bold">
              {allSyncTours.filter(item => item.syncStatus === 'PENDING').length}
            </div>
            <div className="text-sm text-gray-600">Pending</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold">
              {allSyncTours.filter(item => item.syncStatus === 'SYNCED').length}
            </div>
            <div className="text-sm text-gray-600">Synced</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold">
              {allSyncTours.filter(item => item.syncStatus === 'READY').length}
            </div>
            <div className="text-sm text-gray-600">Ready</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Play className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold">
              {allSyncTours.filter(item => item.tourGenerated).length}
            </div>
            <div className="text-sm text-gray-600">Tours Generated</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <XCircle className="w-8 h-8 mx-auto mb-2 text-red-600" />
            <div className="text-2xl font-bold">
              {allSyncTours.filter(item => item.syncStatus === 'FAILED').length}
            </div>
            <div className="text-sm text-gray-600">Failed</div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pending">
            Pending Spaces ({pendingGroups.length})
          </TabsTrigger>
          <TabsTrigger value="all">
            All Spaces ({allGroups.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <div className="space-y-4">
            {pendingGroups.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No pending sync tour items</p>
                </CardContent>
              </Card>
            ) : (
              pendingGroups.map(group => (
                <SpaceGroupCard key={group.spaceId} group={group} showActions={true} />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          <div className="space-y-4">
            {allGroups.map(group => (
              <SpaceGroupCard key={group.spaceId} group={group} showActions={false} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SyncTourManager;

